import { useEffect, useState } from 'react';
import type { Catalog, Episode } from '@tales/story-schema';
import { loadCatalog, loadEpisode } from './lib/content';
import { SessionProvider } from './lib/session';
import { Admin } from './pages/Admin';
import { Home } from './pages/Home';
import { SeriesPage } from './pages/SeriesPage';
import { Player } from './player/Player';

type Route = { name: 'home' } | { name: 'admin' } | { name: 'series'; seriesId: string } | { name: 'play'; seriesId: string; episodeId: string };

function parseRoute(pathname: string): Route {
  const play = /^\/play\/([a-z0-9_-]+)\/([a-z0-9_-]+)\/?$/i.exec(pathname);
  if (play) return { name: 'play', seriesId: play[1]!, episodeId: play[2]! };
  const series = /^\/s\/([a-z0-9_-]+)\/?$/i.exec(pathname);
  if (series) return { name: 'series', seriesId: series[1]! };
  if (/^\/admin\/?$/.test(pathname)) return { name: 'admin' };
  return { name: 'home' };
}

function Shell() {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.pathname));
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [episode, setEpisode] = useState<{ key: string; data: Episode } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    loadCatalog().then(setCatalog, (e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    if (route.name !== 'play') return;
    const key = `${route.seriesId}/${route.episodeId}`;
    if (episode?.key === key) return;
    loadEpisode(route.seriesId, route.episodeId).then(
      (data) => setEpisode({ key, data }),
      (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
    );
  }, [route, episode?.key]);

  const navigate = (to: string) => {
    history.pushState(null, '', to);
    window.scrollTo(0, 0);
    setRoute(parseRoute(to));
  };

  if (error) return <div style={{ padding: 24 }}>Could not load story: {error}</div>;
  if (!catalog) return <div className="spinner" />;

  if (route.name === 'play') {
    const series = catalog.series.find((s) => s.id === route.seriesId);
    if (!episode || episode.key !== `${route.seriesId}/${route.episodeId}`) return <div className="spinner" />;
    const idx = series?.episodes.findIndex((e) => e.id === route.episodeId) ?? -1;
    const nextEp = idx >= 0 ? series?.episodes[idx + 1] : undefined;
    const price = series?.episodes[idx]?.price ?? 0;
    return (
      <Player
        key={episode.key}
        seriesId={route.seriesId}
        seriesTitle={series?.title ?? route.seriesId}
        episode={episode.data}
        price={price}
        episodeIndex={idx}
        nextEpisode={nextEp ? { id: nextEp.id, title: nextEp.title, price: nextEp.price } : undefined}
        onHome={() => navigate(`/s/${route.seriesId}`)}
        onPlayEpisode={(id) => navigate(`/play/${route.seriesId}/${id}`)}
      />
    );
  }

  if (route.name === 'admin') return <Admin catalog={catalog} />;

  if (route.name === 'series') {
    const series = catalog.series.find((s) => s.id === route.seriesId);
    if (!series) return <div style={{ padding: 24 }}>Unknown series.</div>;
    return <SeriesPage series={series} onPlay={(e) => navigate(`/play/${series.id}/${e}`)} onBack={() => navigate('/')} />;
  }

  return <Home catalog={catalog} onOpenSeries={(s) => navigate(`/s/${s}`)} onPlay={(s, e) => navigate(`/play/${s}/${e}`)} />;
}

export function App() {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}
